import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Button, FormControl, MenuItem, Select } from '@material-ui/core';
import { Search } from '@material-ui/icons';
import { ScaleLoader } from 'react-spinners';

import FormTextField from '../../../components/DynamicForm/FormTextField';
import ConversionItem from './ConversionItem';
import { exportFormRegs } from '../../../actions/publishActions';
import { showNotification } from '../../../actions/notificationActions';
import { loadMediasByQueryService } from '../../../services/mediaService';
import { mediaNames as mediaNamesConsts } from '../../../constants/mediaConstants';

import styles from './Conversions.module.scss';

interface ConversionsSectionProps {
    inspectorTheme?: boolean;
    formTemplate: any;
    registrations: any[];
    isLoading: boolean;
    hasMore?: boolean;
    onChange: (_value: any) => void;
    onSearch: (_searchKey: string) => void;
    onLoadMore?: (_searchKey: string) => Promise<any[]>;
};

const ConversionsSection = ({
    inspectorTheme = false,
    formTemplate,
    registrations,
    isLoading,
    hasMore = false,
    onSearch,
    onChange,
    onLoadMore,
}: ConversionsSectionProps) => {
    const { t } = useTranslation();

    const ALL_LABEL = t('COMMON_ALL');
    const ALL_VALUE = '';
    const DELETED_LABEL = t('COMMON_DELETED_MEDIA');
    const DELETED_VALUE = 'deleted-items';

    const dispatch = useDispatch() as any;
    const [searchKey, setSearchKey] = useState<string>('');
    const { defaultAccountId } = useSelector((state: any) => state.session);
    const [currentRegs, setCurrentRegs] = useState<any[]>([]);
    const [filteredRegs, setFilteredRegs] = useState<any[]>([]);
    const [mediaNames, setMediaNames] = useState<{ id: string, name: string }[]>([]);
    const [deletedMediaIds, setDeletedMediaIds] = useState<string[]>([]);
    const [selectedMediaFilter, setSelectedMediaFilter] = useState<string>(ALL_VALUE);
    const [hasMoreLocal, setHasMoreLocal] = useState<boolean>(hasMore);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

    const orderedFields = useMemo(() => {
        if (!formTemplate) {
            return [];
        }
        const fields = formTemplate.styling?.properties || {};
        return Object.keys(fields).sort((a, b) => fields[a].order - fields[b].order);
    }, [formTemplate]);

    const getUniqueMediaIds = useMemo(() => {
        return Array.from(new Set(currentRegs.map(i => i.mediaId).filter(Boolean)));
    }, [currentRegs]);

    const debouncedSearch = useCallback(
        debounce((search: string) => {
            onSearch(search);
        }, 500), []
    );

    const onSearchConversions = (search: string) => {
        setSearchKey(search);
        debouncedSearch(search);
    };

    const refreshConversions = () => {
        setSearchKey('');
        onSearch('');
    };

    const exportFormRegistrations = () => {
        dispatch(exportFormRegs(defaultAccountId, formTemplate.id))
            .then((blob: Blob) => {
                try {
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${formTemplate.name}.xlsx`;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    URL.revokeObjectURL(url);
                } catch(e) {
                    dispatch(showNotification(t('INSPECT_MEDIA_MINI_FORM_EXPORT_ERROR')));
                }
            })
    };

    const removeConversionItem = (id: string) => {
        setCurrentRegs(prev => prev.filter(i => i.id !== id));
        onChange('');
    };

    const handleLoadMore = async () => {
        if (!onLoadMore || isLoadingMore) { return; }
        setIsLoadingMore(true);
        try {
            const newItems = await onLoadMore(searchKey);
            if (newItems && Array.isArray(newItems)) {
                const updatedRegs = [...currentRegs, ...newItems];
                setCurrentRegs(updatedRegs);
                if (selectedMediaFilter === ALL_VALUE) {
                    setFilteredRegs(updatedRegs);
                } else {
                    const valArr = selectedMediaFilter === DELETED_VALUE ? deletedMediaIds : [selectedMediaFilter];
                    setFilteredRegs(updatedRegs.filter((i: any) => valArr.includes(i.mediaId)));
                }
                setHasMoreLocal(newItems.length === 50);
            }
        } finally {
            setIsLoadingMore(false);
        }
    };

    const filterRegistrationsHandler = (event: React.ChangeEvent<{ value: unknown }>) => {
        const value = event.target.value as string;
        setSelectedMediaFilter(value);

        if (value === ALL_VALUE) {
            setFilteredRegs(currentRegs);
        } else {
            const valArr = value === DELETED_VALUE ? deletedMediaIds : [value];
            setFilteredRegs(currentRegs.filter((i: any) => valArr.includes(i.mediaId)));
        }
    };

    const fetchMediaNamesByIds = async (mediaIds: string[]) => {
        const result = (await loadMediasByQueryService({
            accountId: defaultAccountId,
            mediaName: mediaNamesConsts.medias,
            query: `?q=id:(${mediaIds.map(i => `"${i}"`).join(',')})`,
        })) as any;

        const names = [];
        const deletedIds = [];
        for (let i = 0; i < mediaIds.length; i++) {
            const id = mediaIds[i];
            const media = result.find((media: any) => media.id === id);
            const mediaName = media?.metadata?.title;
            if (mediaName) {
                names.push({ id, name: mediaName });
            } else {
                deletedIds.push(id);
            }
        }

        setMediaNames(names);
        setDeletedMediaIds(deletedIds);

        return result;
    };

    useEffect(() => {
        if (getUniqueMediaIds.length) {
            fetchMediaNamesByIds(getUniqueMediaIds);
        }
    }, [getUniqueMediaIds]);

    useEffect(() => {
        setSelectedMediaFilter(ALL_VALUE);
        setCurrentRegs(registrations);
        setFilteredRegs(registrations);
        setHasMoreLocal(hasMore);
    }, [registrations, hasMore]);

    return (
        <div className={`${styles.sectionContainer} ${inspectorTheme ? styles.sectionContainerInpsectorTheme : ''}`}>
            <div className={styles.sectionTop}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={exportFormRegistrations}
                    disabled={!currentRegs.length}
                >
                    {t('COMMON_EXPORT')} .xlsx
                </Button>
            </div>
            <div className={styles.sectionContent}>
                <div className={styles.formTitle}>
                    <FormTextField
                        id="search-conversions"
                        placeholder={true}
                        title={t('MODAL_FORM_INSPECTOR_SEARCH_CONVERSIONS')}
                        value={searchKey}
                        prefixIcon={<Search />}
                        onChange={(_, val: string) => onSearchConversions(val)}
                    />
                </div>
                <div className={styles.formLink}>
                    <button onClick={refreshConversions}>
                        {t('MODAL_FORM_INSPECTOR_UPDATE_LIST')}
                    </button>
                </div>
                <div className={styles.formFilter}>
                    <FormControl variant="outlined" className={styles.selectContainer} fullWidth>
                        <Select
                            value={selectedMediaFilter}
                            onChange={filterRegistrationsHandler}
                            displayEmpty
                            inputProps={{
                                className: styles.selectEl
                            }}
                        >
                            <MenuItem value={ALL_VALUE}>-{ALL_LABEL}-</MenuItem>
                            {deletedMediaIds.length &&
                                <MenuItem value={DELETED_VALUE}>-{DELETED_LABEL}-</MenuItem>
                            }
                            {mediaNames.map(item => (
                                <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
                {isLoading ?
                    <div className={styles.formLoader}>
                        <ScaleLoader color={'#727272'} />
                    </div> :
                    <>
                        {filteredRegs.map((item) => (
                            <ConversionItem
                                key={item.id}
                                formId={formTemplate.id}
                                conversion={item}
                                orderedFields={orderedFields}
                                onChange={removeConversionItem}
                            />
                        ))}
                        {!filteredRegs.length &&
                            <div>{t('MODAL_FORM_INSPECTOR_NO_CONVERSIONS_TXT')}<br /><br /></div>
                        }
                        {onLoadMore && filteredRegs.length > 0 &&
                            <div className={styles.loadMoreContainer}>
                                <Button
                                    variant="outlined"
                                    onClick={handleLoadMore}
                                    disabled={!hasMoreLocal || isLoadingMore}
                                    size="small"
                                >
                                    {isLoadingMore
                                        ? t('COMMON_LOADING')
                                        : hasMoreLocal
                                            ? t('COMMON_LOAD_MORE')
                                            : t('COMMON_NO_MORE_DATA')}
                                </Button>
                            </div>
                        }
                    </>
                }
            </div>
        </div>
    );
};

export default ConversionsSection;
